// Iter 382 (Renzo) — Nómina de sueldos desde Excel (RUT · Nombre · Monto · Concepto).
// Vista previa con cruce por RUT contra trabajadores; el TEF se genera con las mismas reglas que "Genera Nómina".
import { useRef, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { FileSpreadsheet, Loader2, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { EgresoAlegraNominaFields, describirEgresoAlegra, egresoCfgInicial } from './EgresoAlegraNominaFields';

const CLP = (v) => Math.round(v || 0).toLocaleString('es-CL');

export function descargarTxtB64(archivo) {
  const bin = atob(archivo.contenido_b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url; a.download = archivo.filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

function FilaPreview({ f, checked, onToggle }) {
  const ok = f.estado === 'ok';
  return (
    <tr className={`border-t ${!ok ? 'bg-red-50/60 text-muted-foreground' : (checked ? '' : 'opacity-50')}`} data-testid={`excel-fila-${f.n}`}>
      <td className="px-2 py-1 text-center">
        {ok ? <input type="checkbox" checked={checked} onChange={onToggle} data-testid={`excel-check-${f.n}`} /> : <XCircle className="mx-auto h-3.5 w-3.5 text-red-500" />}
      </td>
      <td className="px-2 py-1 font-mono text-[11px]">{f.rut}</td>
      <td className="px-2 py-1">
        <div className="truncate" title={f.nombre_excel}>{f.nombre_excel || <span className="italic text-muted-foreground">sin nombre</span>}</div>
        {ok && f.trabajador_nombre && f.trabajador_nombre.toLowerCase() !== (f.nombre_excel || '').toLowerCase() && (
          <div className="truncate text-[10px] text-muted-foreground">maestro: {f.trabajador_nombre}</div>
        )}
      </td>
      <td className="px-2 py-1 text-[11px]">
        {ok ? (
          <>
            <div className="truncate">{f.slot === 'beneficiario' ? <span className="text-violet-700">→ {f.nombre_beneficiario} ({f.rut_beneficiario})</span> : 'Cuenta principal'}</div>
            <div className="truncate text-muted-foreground">{f.banco_descripcion} · {f.cuenta_numero} · {f.tipo_op}</div>
          </>
        ) : <span className="text-red-700" data-testid={`excel-motivo-${f.n}`}>{f.motivo}</span>}
      </td>
      <td className="px-2 py-1 text-right font-mono">{f.monto ? `$${CLP(f.monto)}` : '—'}</td>
      <td className="px-2 py-1 text-[11px]">
        <div className="truncate" title={f.concepto}>{f.concepto || <span className="italic text-muted-foreground">(motivo general)</span>}</div>
        {f.avisos?.length > 0 && (
          <div className="mt-0.5 flex items-start gap-1 text-[10px] text-amber-700" title={f.avisos.join(' · ')} data-testid={`excel-aviso-${f.n}`}>
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /><span className="line-clamp-2">{f.avisos.join(' · ')}</span>
          </div>
        )}
      </td>
    </tr>
  );
}

export function NominaExcelDialog({ open, onClose, onGenerated }) {
  const fileRef = useRef(null);
  const [cargando, setCargando] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sel, setSel] = useState({});
  const [motivo, setMotivo] = useState('');
  const [generando, setGenerando] = useState(false);
  const [egresoCfg, setEgresoCfg] = useState(egresoCfgInicial());

  const reset = () => { setPreview(null); setSel({}); setMotivo(''); setEgresoCfg((c) => ({ ...egresoCfgInicial(), cuenta_id: c.cuenta_id })); };

  const subir = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setCargando(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/tef/cuentas-colaboradores/nomina-excel/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      setPreview(r.data);
      setSel(Object.fromEntries(r.data.filas.filter((f) => f.estado === 'ok').map((f) => [f.n, true])));
      setMotivo((r.data.concepto_sugerido || '').slice(0, 30));
      if (r.data.n_ok === 0) toast.warning('Ninguna fila del archivo pudo cruzarse con el listado de trabajadores');
      else toast.success(`${r.data.n_ok} fila(s) listas · ${r.data.n_omitidas} omitida(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo leer el archivo');
    } finally { setCargando(false); }
  };

  const filasOk = (preview?.filas || []).filter((f) => f.estado === 'ok');
  const seleccionadas = filasOk.filter((f) => sel[f.n]);
  const total = seleccionadas.reduce((s, f) => s + (f.monto || 0), 0);
  const omitidas = (preview?.filas || []).filter((f) => f.estado !== 'ok');

  const generar = async () => {
    if (!seleccionadas.length) { toast.error('No hay filas seleccionadas'); return; }
    setGenerando(true);
    try {
      const r = await api.post('/tef/cuentas-colaboradores/nomina-excel/generar', {
        lineas: seleccionadas.map((f) => ({ fila_id: f.fila_id, monto: f.monto, concepto: f.concepto || null })),
        motivo, filename: preview.filename, egreso_alegra: egresoCfg,
      });
      if (!r.data.archivo) { toast.info(r.data.mensaje || 'Nada por generar'); return; }
      descargarTxtB64(r.data.archivo);
      toast.success(`${r.data.archivo.filename} descargado · ${r.data.num_lineas} transferencia(s) · $${CLP(r.data.monto_total)}`);
      if (r.data.egreso_alegra) {
        const msg = describirEgresoAlegra(r.data.egreso_alegra);
        if (r.data.egreso_alegra.estado === 'creado') toast.success(msg, { duration: 8000 }); else toast.error(msg, { duration: 10000 });
      }
      onGenerated?.(r.data);
      reset();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error generando la nómina');
    } finally { setGenerando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-5xl" data-testid="dialog-nomina-excel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-700" /> Nómina de sueldos desde Excel</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed bg-slate-50 p-3 text-sm">
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={subir} data-testid="input-excel-nomina" />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={cargando} data-testid="btn-elegir-excel">
              {cargando ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="mr-1 h-3.5 w-3.5" />}
              {preview ? 'Cambiar archivo' : 'Elegir archivo Excel'}
            </Button>
            <span className="text-xs text-muted-foreground">
              Columnas: <b>RUT · Nombre · Monto · Concepto</b> (ej. "Remuneración septiembre 2026"). Se cruza por RUT con el listado de trabajadores; si el trabajador tiene beneficiario alterno anotado, se paga a ese beneficiario. Un mismo RUT puede repetirse (varios pagos en la misma nómina).
            </span>
            {preview && <Badge variant="outline" className="ml-auto" data-testid="excel-filename">{preview.filename}</Badge>}
          </div>

          {preview && (
            <>
              <div className="flex flex-wrap items-center gap-4 text-sm" data-testid="excel-resumen">
                <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> <b>{seleccionadas.length}</b> de {filasOk.length} filas listas</span>
                {omitidas.length > 0 && <span className="flex items-center gap-1 text-red-700"><XCircle className="h-4 w-4" /> <b>{omitidas.length}</b> omitida(s)</span>}
                {preview.n_avisos > 0 && <span className="flex items-center gap-1 text-amber-700"><AlertTriangle className="h-4 w-4" /> {preview.n_avisos} con aviso</span>}
                <span className="ml-auto font-mono text-base font-semibold" data-testid="excel-total">Total: ${CLP(total)}</span>
              </div>
              <div>
                <Label className="text-xs">Motivo general (aparece en el TEF · máx 30 caracteres · las filas con Concepto usan su propio texto)</Label>
                <Input value={motivo} onChange={(e) => setMotivo(e.target.value.slice(0, 30))} placeholder="Ej: Remuneración septiembre 2026" data-testid="input-motivo-excel" />
              </div>
              <EgresoAlegraNominaFields open={open && !!preview} value={egresoCfg} onChange={setEgresoCfg} total={total} testidPrefix="excel-egreso-alegra" />
              <div className="max-h-[50vh] overflow-y-auto rounded border">
                <table className="w-full table-fixed text-xs">
                  <thead className="sticky top-0 bg-muted/80">
                    <tr>
                      <th className="w-8 px-2 py-1.5">
                        <input type="checkbox" checked={filasOk.length > 0 && seleccionadas.length === filasOk.length}
                          onChange={(e) => setSel(Object.fromEntries(filasOk.map((f) => [f.n, e.target.checked])))} data-testid="excel-check-todas" />
                      </th>
                      <th className="w-28 px-2 py-1.5 text-left">RUT</th>
                      <th className="px-2 py-1.5 text-left">Nombre (Excel)</th>
                      <th className="px-2 py-1.5 text-left">Pago a / Banco · Cuenta</th>
                      <th className="w-28 px-2 py-1.5 text-right">Monto</th>
                      <th className="w-56 px-2 py-1.5 text-left">Concepto / Avisos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.filas.map((f) => <FilaPreview key={f.n} f={f} checked={!!sel[f.n]} onToggle={() => setSel({ ...sel, [f.n]: !sel[f.n] })} />)}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }} data-testid="btn-cancelar-excel">Cancelar</Button>
          <Button onClick={generar} disabled={generando || !seleccionadas.length} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-generar-nomina-excel">
            {generando && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            Generar TEF y descargar{seleccionadas.length ? ` (${seleccionadas.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
